import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useState, useMemo } from 'react'
import { Search, Briefcase, Eye, FileText, Clock, CheckCircle2, XCircle, Trash2, MoreHorizontal, Phone, Mail, Loader2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog"
import { useCareer } from '@/hooks/use-forms'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { useAuthStore } from '@/stores/authStore'
import { BASE_URL } from '@/lib/axios'

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

function BasvurularPage() {
  const user = useAuthStore((s) => s.auth.user)
  const isEditor = user?.role?.includes('editor') && !user?.role?.includes('admin') && !user?.role?.includes('super_admin')

  const { data: applications = [], isLoading, updateStatus, deleteApplication } = useCareer()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [viewApp, setViewApp] = useState<any | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [appToDelete, setAppToDelete] = useState<string | null>(null)

  const filteredApplications = useMemo(() => {
    let result = applications

    if (searchTerm) {
      result = result.filter((app: any) =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (app.position || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      result = result.filter((app: any) => app.status === statusFilter)
    }

    return result
  }, [applications, searchTerm, statusFilter])

  const confirmDelete = async () => {
    if (appToDelete) {
      await deleteApplication(appToDelete)
      setDeleteDialogOpen(false)
      setAppToDelete(null)
    }
  }

  const handleUpdateStatus = async (id: string, status: any) => {
    await updateStatus({ id, status })
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'new': return 'default'
      case 'reviewing': return 'secondary'
      case 'accepted': return 'secondary'
      case 'rejected': return 'destructive'
      default: return 'secondary'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'new': return 'Yeni'
      case 'reviewing': return 'İnceleniyor'
      case 'accepted': return 'Uygun'
      case 'rejected': return 'Reddedildi'
      default: return status
    }
  }

  const stats = {
    total: applications.length,
    new: applications.filter((a: any) => a.status === 'new').length,
    reviewing: applications.filter((a: any) => a.status === 'reviewing').length,
    rejected: applications.filter((a: any) => a.status === 'rejected').length
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Başvurular</h1>
            <p className="text-sm text-muted-foreground">Kariyer ve iletişim başvurularını yönetin</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam Başvuru</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Tüm zamanların toplamı</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Yeni</CardTitle>
              <Clock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.new}</div>
              <p className="text-xs text-muted-foreground">Bekleyen başvurular</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">İnceleniyor</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.reviewing}</div>
              <p className="text-xs text-muted-foreground">Süreci devam edenler</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reddedilen</CardTitle>
              <XCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.rejected}</div>
              <p className="text-xs text-muted-foreground">Arşivlenen başvurular</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Başvuru Listesi</CardTitle>
                <CardDescription>Toplam {applications.length} başvuru bulunuyor</CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[150px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Başvuru ara..."
                    className="pl-8 h-9"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Durum" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    <SelectItem value="new">Yeni</SelectItem>
                    <SelectItem value="reviewing">İnceleniyor</SelectItem>
                    <SelectItem value="accepted">Uygun</SelectItem>
                    <SelectItem value="rejected">Reddedildi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? null : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tür</TableHead>
                      <TableHead>Başvuru</TableHead>
                      <TableHead>İsim</TableHead>
                      <TableHead>İletişim</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredApplications.map((application: any) => {
                      return (
                        <TableRow key={application.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">Kariyer</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium text-sm">
                            {application.jobListingId?.title?.tr || application.position || 'Genel Başvuru'}
                          </TableCell>
                          <TableCell className="text-sm">{application.name}</TableCell>
                          <TableCell>
                            <div className="text-xs">
                              <div>{application.email}</div>
                              <div className="text-muted-foreground">{(application as any).phone || 'N/A'}</div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{new Date(application.createdAt).toLocaleDateString('tr-TR')}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusVariant(application.status)}>
                              {getStatusLabel(application.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setViewApp(application)}>
                                  <Eye className="mr-2 h-4 w-4" /> Görüntüle
                                </DropdownMenuItem>
                                {!isEditor && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleUpdateStatus(application.id, 'reviewing')}>
                                      <Clock className="mr-2 h-4 w-4" /> İnceleniyor İşaretle
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleUpdateStatus(application.id, 'accepted')}>
                                      <CheckCircle2 className="mr-2 h-4 w-4" /> Uygun İşaretle
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-destructive" onClick={() => { setAppToDelete(application.id); setDeleteDialogOpen(true) }}>
                                      <Trash2 className="mr-2 h-4 w-4" /> Sil
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Dialog */}
        <Dialog open={!!viewApp} onOpenChange={() => setViewApp(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                {viewApp?.position || viewApp?.jobTitle} Başvurusu
              </DialogTitle>
              <DialogDescription>Başvuru detayları ve aday bilgileri</DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                  {viewApp?.name.split(' ').map((n: string) => n[0]).join('')}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{viewApp?.name}</h3>
                  <div className="flex gap-4 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {viewApp?.email}</span>
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {viewApp?.phone}</span>
                  </div>
                </div>
                <div className="ml-auto">
                  <Badge variant={getStatusVariant(viewApp?.status)}>
                    {getStatusLabel(viewApp?.status)}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase">Başvurulan Pozisyon</span>
                  <p className="font-medium">{viewApp?.jobListingId?.title?.tr || viewApp?.position || 'Genel Başvuru'}</p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase">Başvuru Tarihi</span>
                  <p className="font-medium">{viewApp?.createdAt ? new Date(viewApp.createdAt).toLocaleString('tr-TR') : 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground uppercase">Mesaj / Ön Yazı</span>
                <p className="p-3 bg-muted rounded-md text-sm leading-relaxed whitespace-pre-wrap">
                  {viewApp?.message || 'Mesaj bırakılmamış.'}
                </p>
              </div>

              {viewApp?.resumeUrl && (
                <div className="flex items-center justify-between p-3 border rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">Özgeçmiş Dosyası</span>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href={viewApp.resumeUrl.startsWith('http') ? viewApp.resumeUrl : `${BASE_URL || 'http://localhost:5000'}${viewApp.resumeUrl}`} target="_blank" rel="noopener noreferrer">Görüntüle</a>
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setViewApp(null)}>Kapat</Button>
              {!isEditor && (
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => handleUpdateStatus(viewApp.id, 'accepted')}>Uygun</Button>
                  <Button variant="destructive" onClick={() => handleUpdateStatus(viewApp.id, 'rejected')}>Reddet</Button>
                </div>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Başvuruyu Sil</DialogTitle>
              <DialogDescription>Bu başvuruyu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
              <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Sil</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Main>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/basvurular/')({
  component: BasvurularPage,
})

